
/**
 * Modal to create a new profile and edit a profile
 *
 * @author Reflect-Media <ig@reflect-Media.com>
 * @version 0.0.1
 *
 * @todo [ ] Test the component
 * @todo [ ] Integration test.
 * @todo [✔] Update the typescript.
 *
 */
interface IntervalInterface {
  timeStart: string;
  timeEnd: string;
  startDate: string;
  endDate: string;
  weekdays: Array<number>;
  filename: { name: string; value: string } | null;
  active: boolean;
  cbradioEnabled: boolean;
  prewarningEnabled: boolean;
  holiday: boolean;
}

interface DayOption { day: string; value: number; }

import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapGetters, mapActions, mapMutations } from "vuex";
import Multiselect from "vue-multiselect";
import { ProjectProfile } from "@/interface/projectsInterface";
import confirmModal from "@/mixins/confirmModal/confirmModal";

import IntervalDetailsTable from "@/components/project/SingleProject/Profiles/IntervalDetailsTable.vue";
import { ActionMessage } from "@/model/Messages/statusMessage";

import { WEP_MOCK_URL } from "@/shared/keycloak";

@Component({
  name: "ModalAddProfile",
  components: {
    Multiselect,
    IntervalDetailsTable
  },
  computed: {
    ...mapGetters("ProjectProfilesStore", {
      newProfile: "GetProfileToUpdate"
    })
  },
  methods: {
    ...mapMutations("ProjectProfilesStore", ["RESET_EDIT_PROFILE", "RESET_SECTION_PROFILE"]),
    ...mapActions("ProjectProfilesStore", [
      "GET_ALL_PROJECT_PROFILES_IN_THE_PROJECT",
      "CREATE_NEW_PROFILE",
      "UPDATE_PROJECT_PROFILE",
      "GET_PROFILE_DETAILS"
    ]),
    ...mapActions("projects", {
      createNewInterval: "CreateNewIntervalInSection",
      GET_INTERVAL_DETAILS: "GET_INTERVAL_DETAILS",
      DELETE_iNTERVAL: "DELETE_iNTERVAL"
    })
  }
})
export default class ModalAddProfile extends Mixins(confirmModal) {
  @Prop({
    type: Boolean,
    default: false
  })
  isEditModal!: boolean;
  WEP_MOCK_URL = WEP_MOCK_URL;
  /*----------  Vuex  ----------*/
  newProfile!: ProjectProfile;

  private GetManualOverrideSignOption!: Function;
  private CREATE_NEW_PROFILE!: Function;
  private GET_ALL_PROJECT_PROFILES_IN_THE_PROJECT!: Function;
  private RESET_EDIT_PROFILE!: Function;
  private RESET_SECTION_PROFILE!: Function;

  private UPDATE_PROJECT_PROFILE!: Function;
  private GET_PROFILE_DETAILS!: Function;

  private GET_INTERVAL_DETAILS!: Function;
  private DELETE_iNTERVAL!: Function;

  private createNewInterval!: Function;

  currentStep: number = 1;
  newInterval: IntervalInterface = {
    timeStart: "",
    timeEnd: "",
    startDate: "",
    endDate: "",
    weekdays: [],
    filename: null,
    active: true,
    cbradioEnabled: false,
    prewarningEnabled: false,
    holiday: false
  };

  createProfileStatus: null | ActionMessage = null;

  public get checkRequired(): boolean {
    let isHoliday = this.newInterval.holiday ? true : this.newInterval.weekdays.length > 0;
    const required: null | boolean =
      this.newInterval.timeStart.length > 0 &&
      this.newInterval.timeEnd.length > 0 &&
      this.checkTimeInput() &&
      this.newInterval.filename &&
      isHoliday;
    return required ? false : true;
  }

  checkTimeInput() {
    let reg = /[0-9]{2}:[0-9]{2}/;
    return reg.test(this.newInterval.timeStart) && reg.test(this.newInterval.timeEnd);
  }

  /*----------  local data  ----------*/
  resetIntervalValue() {
    this.newInterval = {
      timeStart: "",
      timeEnd: "",
      startDate: "",
      endDate: "",
      weekdays: [],
      filename: null,
      active: true,
      cbradioEnabled: false,
      prewarningEnabled: false,
      holiday: false
    };
  }

  private normalizeWeekdays(input: Array<number | { value?: number }>): number[] {
    return (input || [])
      .map((d) => (typeof d === "number" ? d : (typeof d?.value === "number" ? d.value : null)))
      .filter((n): n is number => n !== null);
  }


  /*----------  Methods  ----------*/
  handleCreateNewInterval() {
    this.createProfileStatus = null;

    // flache Kopie statt Referenz
    const formattedInterval: any = { ...this.newInterval };

    // <- HIER: keine .value mehr anfassen, sondern numbers übernehmen
    let weekdays: number[] = [];
    if (!formattedInterval.holiday) {
      weekdays = Array.isArray(this.newInterval.weekdays)
        ? this.newInterval.weekdays.slice() // z. B. [1,2,3,4,5,6,7]
        : [];
    }

    // filename sicher auf String normalisieren (dein Multiselect gibt ein Objekt)
    const filename =
      (this.newInterval.filename as any)?.value ??
      (typeof (this.newInterval as any).filename === "string" ? (this.newInterval as any).filename : null);

    this.confirm(
      this.$t("project.section.displayInterval.confirmModals.msgCreateIntervalConfirm").toString(),
      async () => {
        formattedInterval.weekdays = weekdays;
        formattedInterval.filename = filename;
        formattedInterval.profileId = this.newProfile.id;

        const createIntervalStatus = await this.createNewInterval({
          newIntervalToCreate: formattedInterval
        });

        if (createIntervalStatus.status === 201) {
          this.createProfileStatus = {
            class: "success",
            msg: this.$t("project.section.displayInterval.confirmModals.msgCreatedSuccessfully").toString()
          };
          this.GET_PROFILE_DETAILS(this.newProfile.id);
          this.resetIntervalValue();
        } else {
          this.createProfileStatus = {
            class: "danger",
            msg: this.$t("project.section.displayInterval.confirmModals.msgCreatedError").toString()
          };
        }
      },
      () => {
        this.resetIntervalValue();
      }
    );
  }

  handleCancelCreateInterval() {
    this.resetIntervalValue();
  }

  // Profile
  async handleSubmitProfile() {
    this.$emit("updateStatus", null);
    this.createProfileStatus = null;

    if (this.isEditModal) {
      this.handleUpdateProfile();
    } else {
      this.handleCreateNewProfile();
    }
  }
  async handleCreateNewProfile() {
    this.newProfile.projectId = this.$route.params.id;

    let createProfile = await this.CREATE_NEW_PROFILE(this.newProfile);

    if (createProfile.status === 201) {
      this.GET_PROFILE_DETAILS(createProfile.data.id);
      this.createProfileStatus = {
        class: "success",
        msg: this.$t("project.profiles.actionsMsg.createSuccess").toString()
      };
      this.currentStep = 2;
    } else {
      this.createProfileStatus = {
        class: "danger",
        msg: this.$t("project.profiles.exception.unique_name").toString()
      };
    }
  }

  async handleUpdateProfile() {
    let updateProfile = await this.UPDATE_PROJECT_PROFILE({
      id: this.newProfile.id,
      profileToUpdate: this.newProfile
    });
    if (updateProfile.status === 200) {
      this.createProfileStatus = {
        class: "success",
        msg: this.$t("project.profiles.actionsMsg.updateSuccess").toString()
      };

      this.GET_PROFILE_DETAILS(this.newProfile.id);
      this.currentStep = 2;
    } else {
      this.createProfileStatus = {
        class: "danger",
        msg: updateProfile
      };
    }
  }

  handleModalClose() {
    this.GET_ALL_PROJECT_PROFILES_IN_THE_PROJECT(this.$route.params.id);
    this.RESET_EDIT_PROFILE();
    this.RESET_SECTION_PROFILE();
    this.currentStep = 1;
    this.createProfileStatus = null;
    this.newInterval = {
      timeStart: "",
      timeEnd: "",
      startDate: "",
      endDate: "",
      weekdays: [],
      filename: null,
      active: true,
      cbradioEnabled: false,
      prewarningEnabled: false,
      holiday: false
    };
  }

  handleDeleteStatus(status: ActionMessage) {
    this.createProfileStatus = status;
  }

  HandleAddCustomImage(customFilename: string, settings: any) {
    let filename = customFilename.replace(/.bmp/g, "");
    settings.filename = {
      name: filename,
      value: `${filename}.bmp`
    };
  }

  // Optionen aus i18n lesen
  get weekdayOptions(): DayOption[] {
    const opts = this.$t("project.section.displayInterval.schedulingDaysOptions") as unknown as DayOption[] | undefined;
    return Array.isArray(opts) ? opts : [];
  }

// Proxy zwischen number[] im State und Objekt-Array im Multiselect
  get weekdayProxy(): DayOption[] {
    const byVal = new Map(this.weekdayOptions.map(o => [o.value, o]));
    return this.newInterval.weekdays
      .map(v => byVal.get(v))
      .filter(Boolean) as DayOption[];
  }

  set weekdayProxy(selected: DayOption[]) {
    this.newInterval.weekdays = selected.map(s => s.value);
  }

// "Dauer"-Button Logik
  setFullDay() {
    this.newInterval.timeStart = "00:00:00";
    this.newInterval.timeEnd = "23:59:59";
    this.newInterval.holiday = false;

    // Alle Werte 1–7 aus den Optionen setzen
    this.newInterval.weekdays = this.weekdayOptions.map(o => o.value);
  }
}
